import { useState, useEffect } from "react";
import Icon from "../Icon";
import { ModalFt } from "../Atoms";
import DatePicker from "./DatePicker";
import * as api from "../../api";
import { diasDelPeriodo, esFinde } from "../../utils";
import Campo from "./Campo";

const TIPOS = [
  { v:"vacaciones", label:"Vacaciones" },
  { v:"permiso",    label:"Permiso"    },
  { v:"baja",       label:"Baja"       },
];

// Un periodo de ausencia. Un usuario normal solo puede registrar los suyos;
// un admin elige de quién son (el backend vuelve a comprobarlo).
export default function VacacionesForm({
  periodo = null, users = [], currentUser, isAdmin = false, festivos = {},
  defaultDate = "", onSave, onSolicitado, onCrearYSolicitar, onDelete, onClose,
}) {
  const isNew = !periodo?.id;
  const [f, setF] = useState({
    user_id:   periodo?.user_id   || currentUser?.id || "",
    fecha:     periodo?.fecha     || defaultDate || "",
    fecha_fin: periodo?.fecha_fin || "",
    tipo:      periodo?.tipo      || "vacaciones",
    nota:      periodo?.nota      || "",
    // El tipo de verdad es el de Odoo: son los 16 que maneja RRHH. El `tipo` de
    // arriba solo decide el color del calendario y lo deduce el backend del
    // nombre, para que no puedan contradecirse.
    odoo_tipo_id:     periodo?.odoo_tipo_id ? String(periodo.odoo_tipo_id) : "",
    odoo_tipo_nombre: periodo?.odoo_tipo_nombre || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setF(p => ({...p,[k]:v}));

  // ── Solicitud a RRHH ────────────────────────────────────────────────────────
  // Registrar aqui NO es pedir: sirve para que el equipo se organice. La
  // peticion es un segundo paso explicito que crea la ausencia en Odoo, que es
  // donde RRHH valida. Por eso esto solo sale sobre un periodo ya guardado.
  const estado = periodo?.estado || (isNew ? "borrador" : "aprobada");
  const esBorrador = !isNew && estado === "borrador";

  const [tipos, setTipos]   = useState(null);   // null = aun cargando
  const [errTipos, setErrTipos] = useState(null);
  const [pidiendo, setPidiendo] = useState(false);

  // Los tipos se piden siempre —tambien al crear—, porque son EL selector de
  // tipo. Si Odoo no responde o la cuenta no esta conectada, el formulario cae a
  // los tres de ALM y sigue funcionando.
  const puedeEditarTipoOdoo = isNew || estado === "borrador";
  useEffect(() => {
    if (!puedeEditarTipoOdoo || !f.user_id) return;
    let vivo = true;
    setTipos(null); setErrTipos(null);
    // Los de ESA persona: el cupo se lee con la sesion de cada uno, asi que
    // preguntar con la propia daria los cupos propios.
    api.getTiposOdoo(String(f.user_id) === String(currentUser?.id) ? null : f.user_id)
      .then(t => vivo && setTipos(t))
      .catch(e => vivo && setErrTipos(e.message || "No se pudieron leer los tipos de Odoo"));
    return () => { vivo = false; };
  }, [puedeEditarTipoOdoo, f.user_id, currentUser]);

  // Se propone por NOMBRE y AÑO, nunca por id: los tipos son "Vacaciones 2026" y
  // el id cambia cada enero. Y si no hay ninguno de ese año NO se propone nada:
  // proponer "Vacaciones 2025" para un periodo de 2027, como hacia antes, es
  // peor que no proponer.
  useEffect(() => {
    if (!tipos || f.odoo_tipo_id) return;
    const anio = String(f.fecha || "").slice(0, 4);
    if (!anio) return;
    const cand = tipos.find(x => x.nombre.toUpperCase().startsWith("VACACIONES") && x.nombre.includes(anio));
    if (cand) setF(p => ({ ...p, odoo_tipo_id: String(cand.id), odoo_tipo_nombre: cand.nombre }));
  }, [tipos, f.fecha, f.odoo_tipo_id]);

  // ¿Se puede mandar a RRHH con el tipo elegido? Lo miran dos sitios: el bloque
  // de un borrador ya guardado y el botón de "registrar y solicitar" del alta.
  const tipoSel      = (tipos || []).find(x => String(x.id) === String(f.odoo_tipo_id)) || null;
  const ajenoConCupo = !!tipoSel && tipoSel.solicitable === false;
  // Sin días disponibles no hay solicitud: se queda en borrador.
  const agotado      = !!tipoSel && tipoSel.con_cupo && tipoSel.quedan !== null && tipoSel.quedan <= 0;
  const puedeSolicitar = !!tipoSel && !ajenoConCupo && !agotado;

  const elegirTipoOdoo = (id) => {
    const t = (tipos || []).find(x => String(x.id) === String(id));
    setF(p => ({ ...p, odoo_tipo_id: id, odoo_tipo_nombre: t ? t.nombre : "" }));
  };

  const solicitar = async () => {
    if (!f.odoo_tipo_id) return;
    setPidiendo(true);
    try {
      // Ojo: NO se llama a onSave. Ese guarda el formulario y dispararia un PUT
      // encima de la fila que el backend acaba de devolver ya actualizada.
      const act = await api.solicitarVacacion(periodo.id, Number(f.odoo_tipo_id));
      onSolicitado?.(act);
    } catch (e) {
      setErrTipos(e.message || "No se pudo enviar la solicitud");
    } finally {
      setPidiendo(false);
    }
  };

  // Sin fecha de fin es un solo día; con ella, un periodo. No hace falta marcar
  // nada aparte: la propia casilla de "Hasta" ya dice cuál de los dos es.
  const errFin = f.fecha_fin && f.fecha_fin < f.fecha;
  const valid  = !!f.fecha && !!f.user_id && !errFin;

  // Lo que de verdad consume: los días laborables del periodo
  const dias = valid ? diasDelPeriodo(f.fecha, f.fecha_fin || null) : [];
  const laborables = dias.filter(d => !esFinde(d) && !festivos[d]).length;

  const lbS  = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
  const inS  = {width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)"};
  const secS = {marginBottom:14};

  const datos = () => ({
    user_id:   f.user_id,
    fecha:     f.fecha,
    fecha_fin: f.fecha_fin || null,
    tipo:      f.tipo,
    nota:      f.nota.trim() || null,
    odoo_tipo_id:     f.odoo_tipo_id ? Number(f.odoo_tipo_id) : null,
    odoo_tipo_nombre: f.odoo_tipo_nombre || null,
  });

  // Registrar y solicitar de una tacada. Son dos pasos de verdad —primero nace
  // el borrador y luego se manda—, y si el envío falla el borrador SE QUEDA:
  // eso es lo que se quiere, el periodo ya está apuntado y se puede reintentar
  // desde él sin volver a escribirlo.
  const crearYSolicitar = async () => {
    if (!valid || saving || !puedeSolicitar) return;
    setSaving(true);
    try { await onCrearYSolicitar(datos(), Number(f.odoo_tipo_id)); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onSave({
        user_id:   f.user_id,
        fecha:     f.fecha,
        fecha_fin: f.fecha_fin || null,
        tipo:      f.tipo,
        nota:      f.nota.trim() || null,
        odoo_tipo_id:     f.odoo_tipo_id ? Number(f.odoo_tipo_id) : null,
        odoo_tipo_nombre: f.odoo_tipo_nombre || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h3 style={{margin:"0 0 4px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
        <Icon name={isNew?"plus-circle":"pencil-square"} size={15} color="var(--accent)"/>
        {isNew ? "Nuevo periodo" : "Editar periodo"}
      </h3>
      <p style={{margin:"0 0 16px",fontSize:11.5,color:"var(--text3)",lineHeight:1.5}}>
        Vacaciones, permisos y bajas del equipo.
      </p>

      {isAdmin ? (
        <div style={secS}>
          <Campo><label style={lbS}>Persona</label>
          <select value={f.user_id} onChange={e=>set("user_id", e.target.value)} style={inS}>
            {[...users].sort((a,b)=>(a.name||"").localeCompare(b.name||""))
              .map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select></Campo>
        </div>
      ) : (
        <div style={secS}>
          <Campo><label style={lbS}>Persona</label>
          <div style={{...inS,color:"var(--text3)",background:"var(--bg)"}}>{currentUser?.name}</div></Campo>
        </div>
      )}

      <div style={{...secS,display:"flex",gap:10}}>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>Desde</label>
          <DatePicker value={f.fecha} onChange={v=>set("fecha", v)}/></Campo>
        </div>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>Hasta <span style={{fontWeight:500,textTransform:"none",letterSpacing:0}}>· opcional</span></label>
          <DatePicker value={f.fecha_fin} onChange={v=>set("fecha_fin", v)} refDate={f.fecha}/></Campo>
        </div>
      </div>
      <div style={{fontSize:10.5,color:"var(--text3)",margin:"-8px 0 14px",lineHeight:1.4}}>
        Deja «Hasta» en blanco para un solo día.
      </div>

      {/* El tipo son los de Odoo, que es donde RRHH los gestiona: son 16 y
          distinguen cosas que importan (una baja por accidente de trabajo no es
          lo mismo que una por enfermedad común). Los tres de ALM —Vacaciones,
          Permiso, Baja— solo pintan el calendario y los deduce el backend del
          nombre, para que no puedan contradecirse. */}
      <div style={secS}>
        <label style={lbS} htmlFor="vac-tipo-odoo">Tipo</label>

        {tipos && tipos.length > 0 && puedeEditarTipoOdoo && (
          <>
            <select id="vac-tipo-odoo" value={f.odoo_tipo_id} onChange={e=>elegirTipoOdoo(e.target.value)} style={inS}>
              <option value="">Elige el tipo…</option>
              {tipos.map(t => (
                <option key={t.id} value={t.id} disabled={t.solicitable === false}>
                  {t.nombre}
                  {t.con_cupo && t.quedan !== null ? `  ·  quedan ${t.quedan} de ${t.total}` : ""}
                  {t.solicitable === false ? "  ·  solo puede pedirlo la persona" : ""}
                </option>
              ))}
            </select>
            {!f.odoo_tipo_id && (
              <div style={{fontSize:10.5,color:"var(--text3)",marginTop:5,lineHeight:1.4}}>
                Elige el tipo con el que RRHH lo va a ver en Odoo.
              </div>
            )}
            {tipos.some(t => t.ajeno) && (
              <div style={{fontSize:10.5,color:"var(--text3)",marginTop:5,lineHeight:1.4}}>
                Los tipos que descuentan del cupo anual solo puede pedirlos la
                propia persona: tú no ves cuántos días le quedan. Sí puedes
                tramitarle bajas, citaciones y demás ausencias sin máximo.
              </div>
            )}
          </>
        )}

        {/* Sin Odoo delante no se puede ofrecer su lista, pero el periodo debe
            poder registrarse igual: para eso están los tres de siempre. */}
        {(!tipos || tipos.length === 0 || !puedeEditarTipoOdoo) && (
          <>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {TIPOS.map(t => (
                <button key={t.v} className="btn" onClick={()=>set("tipo", t.v)}
                  disabled={!puedeEditarTipoOdoo}
                  style={{padding:"6px 14px",borderRadius:8,fontSize:12,fontFamily:"var(--font)",
                    cursor: puedeEditarTipoOdoo ? "pointer" : "default",
                    border:`1px solid ${f.tipo===t.v?"var(--accent)":"var(--border)"}`,
                    background:f.tipo===t.v?"var(--accent-bg)":"var(--surface2)",
                    color:f.tipo===t.v?"var(--accent)":"var(--text2)",
                    opacity: puedeEditarTipoOdoo ? 1 : .6,
                    fontWeight:f.tipo===t.v?700:500}}>
                  {t.label}
                </button>
              ))}
            </div>
            {puedeEditarTipoOdoo && errTipos && (
              <div style={{fontSize:10.5,color:"var(--text3)",marginTop:5,lineHeight:1.4}}>
                No se han podido leer los tipos de Odoo ({errTipos}). Puedes registrarlo
                igualmente; el tipo exacto se elegirá al solicitarlo.
              </div>
            )}
            {!puedeEditarTipoOdoo && f.odoo_tipo_nombre && (
              <div style={{fontSize:10.5,color:"var(--text3)",marginTop:5}}>
                En Odoo: <b>{f.odoo_tipo_nombre}</b>
              </div>
            )}
          </>
        )}
      </div>

      <div style={secS}>
        <Campo><label style={lbS}>Nota (opcional)</label>
        <input value={f.nota} onChange={e=>set("nota", e.target.value)} style={inS}
          placeholder="Puente, asuntos propios…"/></Campo>
      </div>

      {valid && dias.length > 0 && (
        <div style={{fontSize:11.5,color:"var(--text2)",background:"var(--surface2)",border:"1px solid var(--border)",
          borderRadius:9,padding:"9px 11px",marginBottom:14,lineHeight:1.45}}>
          {dias.length} día{dias.length===1?"":"s"} natural{dias.length===1?"":"es"} · <b>{laborables} laborable{laborables===1?"":"s"}</b>
          <div style={{fontSize:10.5,color:"var(--text3)",marginTop:2}}>
            Fines de semana y festivos del calendario laboral no cuentan.
          </div>
        </div>
      )}

      {errFin && (
        <div style={{fontSize:11.5,color:"var(--red-text)",marginBottom:12}}>
          La fecha de fin no puede ser anterior al inicio.
        </div>
      )}

      {/* ── Estado ante RRHH ─────────────────────────────────────────────── */}
      {isNew && (
        <div style={{fontSize:10.5,color:"var(--text3)",marginBottom:12,lineHeight:1.45}}>
          Al registrarlo queda como <b>borrador</b>: lo ve el equipo y cuenta para
          el reparto de guardias, pero todavía no es una petición ni te libera de
          fichar. Después podrás solicitarlo a RRHH desde aquí mismo.
        </div>
      )}

      {esBorrador && (
        <div style={{background:"var(--surface2)",border:"1px solid var(--border)",
          borderRadius:9,padding:"10px 11px",marginBottom:14}}>
          <div style={{fontSize:11.5,color:"var(--text2)",marginBottom:8,lineHeight:1.45}}>
            <b>Borrador.</b> RRHH todavía no lo ha visto. Al solicitarlo se crea la
            ausencia en Odoo con el tipo de arriba y queda pendiente de su validación.
          </div>

          {errTipos && (
            <div style={{fontSize:11,color:"var(--red-text)",marginBottom:8}}>{errTipos}</div>
          )}

          {(() => {
            const t = tipoSel;
            const puede = puedeSolicitar && !pidiendo;
            return (
              <>
                {!f.odoo_tipo_id && (
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>
                    Elige antes el tipo, arriba.
                  </div>
                )}
                {ajenoConCupo && (
                  <div style={{fontSize:11,color:"var(--red-text)",marginBottom:8,lineHeight:1.4}}>
                    «{t.nombre}» descuenta del cupo anual, así que tiene que pedirlo
                    la propia persona.
                  </div>
                )}
                {agotado && (
                  <div style={{fontSize:11,color:"var(--red-text)",marginBottom:8,lineHeight:1.4}}>
                    No quedan días en «{t.nombre}». Sin saldo no se puede solicitar:
                    el periodo se queda en borrador, visible para el equipo.
                    {(tipos || []).some(x => x.con_cupo && x.quedan > 0) && (
                      <> Hay otro tipo con días disponibles, por si corresponde.</>
                    )}
                  </div>
                )}
                {/* Con saldo pero corto, avisa y deja enviar: eso ya lo decide RRHH. */}
                {t && t.con_cupo && t.quedan !== null && t.quedan > 0 && t.quedan < laborables && !ajenoConCupo && (
                  <div style={{fontSize:11,color:"var(--red-text)",marginBottom:8,lineHeight:1.4}}>
                    Pides {laborables} día{laborables===1?"":"s"} y quedan {t.quedan}.
                    Puedes enviarlo igualmente, pero RRHH lo verá con ese saldo.
                  </div>
                )}
                <button className="btn" onClick={solicitar} disabled={!puede}
                  style={{width:"100%",padding:"9px 14px",borderRadius:9,border:"none",
                    cursor: puede ? "pointer" : "not-allowed",
                    fontSize:12.5,fontWeight:700,fontFamily:"var(--font)",
                    background: puede ? "var(--accent)" : "var(--surface)",
                    color: puede ? "#fff" : "var(--text3)",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                  <Icon name="box-arrow-right" size={12} color={puede ? "#fff" : "var(--text3)"}/>
                  {pidiendo ? "Enviando a RRHH…" : "Solicitar a RRHH"}
                </button>
              </>
            );
          })()}
        </div>
      )}

      {!isNew && estado !== "borrador" && (
        <div style={{fontSize:11.5,marginBottom:14,padding:"9px 11px",borderRadius:9,lineHeight:1.45,
          background:"var(--surface2)",border:"1px solid var(--border)",color:"var(--text2)"}}>
          {estado === "solicitada" && <>
            <b>Pendiente de aprobar.</b> Está en Odoo esperando a RRHH. Se ve
            translúcido en el calendario y cuenta para el reparto de guardias,
            pero aún no te libera de fichar.
          </>}
          {estado === "aprobada" && <>
            <b>Aprobada por RRHH.</b> Firme: estos días ya no te exigen horas.
          </>}
          {estado === "rechazada" && <>
            <b>Rechazada por RRHH</b> en Odoo.
          </>}
          {periodo?.odoo_tipo_nombre && (
            <div style={{fontSize:10.5,color:"var(--text3)",marginTop:3}}>
              {periodo.odoo_tipo_nombre}
              {periodo.odoo_dias != null ? ` · ${periodo.odoo_dias} día(s) según Odoo` : ""}
            </div>
          )}
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
        {!isNew && onDelete && (
          <button className="btn" onClick={onDelete} disabled={saving}
            style={{padding:"8px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",
              fontSize:12.5,background:"var(--surface2)",color:"var(--red-text)",fontFamily:"var(--font)",
              display:"flex",alignItems:"center",gap:6}}>
            <Icon name="trash3" size={12} color="var(--red-text)"/> Eliminar
          </button>
        )}
        <div style={{flex:1}}/>
        {/* Al dar de alta, el atajo: crea el borrador y lo manda en el mismo
            gesto. Solo aparece cuando el envío es posible de verdad —hay tipo,
            hay saldo y es tuyo—, para no ofrecer un botón que va a fallar. */}
        {isNew && puedeSolicitar && (
          <button className="btn" onClick={crearYSolicitar} disabled={!valid || saving}
            style={{padding:"8px 14px",borderRadius:9,cursor: valid && !saving ? "pointer" : "not-allowed",
              fontSize:12.5,fontFamily:"var(--font)",fontWeight:700,
              border:"1px solid var(--accent)", background:"var(--accent-bg)", color:"var(--accent)",
              display:"flex",alignItems:"center",gap:6}}>
            <Icon name="box-arrow-right" size={12} color="var(--accent)"/>
            {saving ? "Enviando…" : "Registrar y solicitar"}
          </button>
        )}
        <ModalFt onClose={onClose} label={saving ? "Guardando…" : (isNew ? "Registrar" : "Guardar cambios")}
          onSave={submit} disabled={!valid || saving}/>
      </div>
    </div>
  );
}
